import { useEffect, useState } from 'react';
import { api } from '../api.js';
import { toast } from './Toasts.jsx';
import ChipInput from './ChipInput.jsx';

// T083 — Inline preferences pill. Sits at the top of the dashboard
// showing "Hunting: <roles> in <locations> · edit". Clicking "edit"
// opens an inline panel with the same chip inputs the Profile page
// uses — no page navigation needed for the most common tweak.
//
// We only surface roles + locations (the two preferences that change
// most often during active job hunting). Seniority, remote preference,
// and salary floor stay on /profile — they're tuned once and forgotten.
//
// On save, calls onPrefsUpdated so the parent can refresh user state
// (which the auth context owns).

export default function HuntingPill({ user, onPrefsUpdated }) {
  const [editing, setEditing] = useState(false);
  const initRoles = user?.preferences?.targetRoles ?? [];
  const initLocations = user?.preferences?.targetLocations ?? [];
  const [roles, setRoles] = useState(initRoles);
  const [locations, setLocations] = useState(initLocations);
  const [saving, setSaving] = useState(false);

  // Reset local state when the source of truth updates (e.g. saved from
  // profile page in another tab and AuthContext refreshes).
  useEffect(() => {
    setRoles(user?.preferences?.targetRoles ?? []);
    setLocations(user?.preferences?.targetLocations ?? []);
  }, [user?.preferences?.targetRoles, user?.preferences?.targetLocations]);

  const noPrefs = initRoles.length === 0 && initLocations.length === 0;
  const dirty =
    JSON.stringify(roles) !== JSON.stringify(initRoles) ||
    JSON.stringify(locations) !== JSON.stringify(initLocations);

  const save = async () => {
    if (!dirty) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await api.patch('/preferences', {
        targetRoles: roles,
        targetLocations: locations,
      });
      await onPrefsUpdated?.();
      setEditing(false);
      toast('Preferences saved', { kind: 'success' });
    } catch (e) {
      toast(e?.body?.message || e?.message || 'Save failed', { kind: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setRoles(initRoles);
    setLocations(initLocations);
    setEditing(false);
  };

  if (editing) {
    return (
      <section className="mt-4 rounded-xl border border-brand-700 bg-brand-600/5 p-4">
        <div className="flex items-baseline justify-between gap-2">
          <p className="text-xs font-medium text-brand-400">Edit hunt filters</p>
          <button
            type="button"
            onClick={cancel}
            disabled={saving}
            className="text-[11px] text-slate-500 underline decoration-dotted hover:text-slate-300 disabled:opacity-50"
          >
            cancel
          </button>
        </div>
        <div className="mt-2 grid gap-3 sm:grid-cols-2">
          <div>
            <label className="text-[10px] uppercase tracking-wide text-slate-400">Target roles</label>
            <ChipInput value={roles} onChange={setRoles} placeholder="Cloud Engineer, DevOps" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wide text-slate-400">Target locations</label>
            <ChipInput value={locations} onChange={setLocations} placeholder="Copenhagen, Remote Europe" />
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <button
            type="button"
            onClick={save}
            disabled={saving || !dirty}
            className="rounded-md bg-brand-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-500 disabled:opacity-50"
          >
            {saving ? 'saving…' : 'Save filters'}
          </button>
          <p className="text-[11px] text-slate-500">
            Seniority, remote, salary floor on{' '}
            <a href="/profile" className="underline decoration-dotted hover:text-brand-400">Profile</a>.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="mt-4 flex flex-wrap items-baseline gap-2 rounded-lg border border-slate-800 bg-slate-900/30 px-3 py-2 text-xs">
      <span className="text-slate-500">Hunting:</span>
      {noPrefs ? (
        <span className="text-amber-400">no filters set — every scanned job will be scored, but most won't match.</span>
      ) : (
        <>
          <span className="text-slate-300">
            {initRoles.length > 0 ? initRoles.join(', ') : <em className="text-slate-600">any role</em>}
          </span>
          <span className="text-slate-600">in</span>
          <span className="text-slate-300">
            {initLocations.length > 0 ? initLocations.join(', ') : <em className="text-slate-600">any location</em>}
          </span>
        </>
      )}
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="ml-auto rounded border border-slate-700 px-2 py-0.5 text-[11px] text-slate-300 hover:border-brand-600 hover:text-brand-400"
      >
        edit
      </button>
    </section>
  );
}
